import type { Active, DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  ENavigationIcon,
  EProjectPermission,
  ESidebarView,
  getSidebarViews,
  isSidebarView,
  resolveSidebarView,
} from "@frontend-shared/types";
import { useDirection } from "@radix-ui/react-direction";
import { GripVertical } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, NavLink, useLocation } from "react-router-dom";

import { useArchiveChat } from "@/api/chat/useArchiveChat";
import { useMoveToFolder } from "@/api/chat/useMoveToFolder";
import { useUnarchiveChat } from "@/api/chat/useUnarchiveChat";
import { useGetEnabledIntegrations } from "@/api/integration/useGetEnabledIntegrations";
import { NavigationIcon, sidebarNavIconVariants } from "@/components/icons";
import { Logo } from "@/components/Logo";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { useTheme } from "@/components/ui/theme-provider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PATHS } from "@/constants/paths";
import { isNodeShiftFlavor, isOplotFlavor } from "@/env/env";
import { useAgentsEnabled } from "@/hooks/use-agents-enabled";
import { useAnonymizationLabEnabled } from "@/hooks/use-anonymization-lab-enabled";
import { useAuth } from "@/hooks/use-auth";
import { useCallAssistantFeaturesEnabled } from "@/hooks/use-call-assistant-features-enabled";
import { useCustomIntegrationEnabled } from "@/hooks/use-custom-integration-enabled";
import { useDocRecognitionEnabled } from "@/hooks/use-doc-recognition-enabled";
import { useMainTour } from "@/hooks/use-main-tour";
import { usePermissionCheck } from "@/hooks/use-permission";
import { useSidebarView } from "@/hooks/use-sidebar-view";
import { useSkillPermissions } from "@/hooks/use-skill-permissions";
import { useSkillsEnabled } from "@/hooks/use-skills-enabled";
import { shouldShowPlatformLogomark } from "@/lib/branding/should-show-platform-logomark";
import { cn } from "@/lib/utils";

import { ChatFolderList } from "./ChatFolderList";
import { ChatList } from "./ChatList";
import { ArchivedChats } from "./ChatList/components/ArchivedChats";
import { SidebarViewSwitch } from "./SidebarViewSwitch";
import { UploadsInProgressBadge } from "./UploadsInProgressBadge";
import { UserMenu } from "./UserMenu";

// NOTE: dnd-kit types a draggable's payload as an open record, so this is the
// shape the sidebar reads back off it — the same one every `ChatListItem`
// attaches.
type TDragData = NonNullable<Active["data"]["current"]>;

// NOTE: NSAI-5822 — one class for every nav row so the expanded list and the
// collapsed rail cannot drift apart. Expanded it is a 36px pill; collapsed the
// base `sidebarMenuButtonVariants` rule forces `!size-8`, which left a 32px
// button pinned to the start of an 80px rail — the icons read as a ragged
// left-hand column rather than a centred rail. The overrides below restore a
// 40px hit target and centre it in the rail.
//
// The label is `display: none` while collapsed, which removes it from the
// accessibility tree along with the screen, so every row names itself with an
// `aria-label` as well: the rail's tooltip is a description, not a name, and a
// screen reader would otherwise land on a run of unnamed buttons.
const NODESHIFT_RAIL_MARK_URL = "/logo-mark.svg";
const OPLOT_RAIL_MARK_URL = "/oplot-favicon-light.svg";

const NAV_BUTTON_CLASS = [
  "h-9 gap-2.5 rounded-xl px-2.5",
  "transition-[background-color,color] duration-200 ease-out",
  "group-data-[collapsible=icon]:!size-10 group-data-[collapsible=icon]:!p-0",
  "group-data-[collapsible=icon]:mx-auto group-data-[collapsible=icon]:justify-center",
  "group-data-[collapsible=icon]:[&>span:first-of-type]:hidden",
].join(" ");

const MainSidebarComponent = (): JSX.Element => {
  const { t } = useTranslation("sidebar");
  const dir = useDirection();
  const { isMobile, state: sidebarState } = useSidebar();
  const { customFaviconUrl, hasOwnBranding, isBrandingResolvedForProject } =
    useTheme();
  // NOTE: NSAI-6651 — the collapsed rail shows the organization's mark at its
  // top for EVERY organization, not only the ones that uploaded a favicon: the
  // NodeShift build and every organization on the default appearance used to
  // keep the bare panel glyph there and never got the logo-with-toggle-on-hover
  // NSAI-6475 introduced. Without an upload it is the flavour's own mark, drawn
  // from its SVG rather than the 32px `.ico` in the tab.
  //
  // Who may see NodeShift's mark is `shouldShowPlatformLogomark`, the same gate
  // the empty chat uses (NSAI-6427): the NodeShift build, no branding of the
  // organization's own, and a branding answer for THIS project (the
  // project-keyed flag, for the switch race `ChatEmptyLogo` documents). Oplot's
  // build shows Oplot's mark under the same two organization conditions. Any
  // other case keeps the toggle — a vendor's mark is never drawn over a
  // tenant's brand.
  const isOrgOnDefaultAppearance =
    isBrandingResolvedForProject && !hasOwnBranding;
  const flavourMarkUrl = shouldShowPlatformLogomark({
    isNodeShiftFlavor: isNodeShiftFlavor(),
    isBrandingResolved: isBrandingResolvedForProject,
    hasOwnBranding,
  })
    ? NODESHIFT_RAIL_MARK_URL
    : isOplotFlavor() && isOrgOnDefaultAppearance
      ? OPLOT_RAIL_MARK_URL
      : null;
  const railFaviconUrl = customFaviconUrl ?? flavourMarkUrl;
  const [loadedFaviconUrl, setLoadedFaviconUrl] = useState<string | null>(null);
  const [isHoverRevealArmed, setIsHoverRevealArmed] = useState(true);
  const [isKeyboardFocusVisible, setIsKeyboardFocusVisible] = useState(false);
  const isFaviconReady =
    railFaviconUrl !== null && loadedFaviconUrl === railFaviconUrl;
  const { isRunning } = useMainTour();
  const { isPersonalProject } = useAuth();
  const [
    hasModelReadPermission,
    hasPrivateAssistantReadPermission,
    hasPromptLibraryReadPermission,
    hasAllMembersAnalyticsReadPermission,
    hasAnalyticsReadPermission,
    hasProbeReadPermission,
    hasIntegrationReadPermission,
    hasKnowledgeReadPermission,
    hasAutomationReadPermission,
    hasAutomationManagerReadPermission,
    hasPublicAssistantReadPermission,
    hasCustomIntegrationReadPermission,
    hasCallAssistantReadPermission,
    hasDocRecognitionReadPermission,
    hasDashboardReadPermission,
  ] = usePermissionCheck([
    EProjectPermission.MODEL_READ,
    EProjectPermission.PRIVATE_ASSISTANT_READ,
    EProjectPermission.PROMPT_LIBRARY_READ,
    EProjectPermission.ALL_MEMBERS_ANALYTICS_READ,
    EProjectPermission.ANALYTICS_READ,
    EProjectPermission.PROBE_READ,
    EProjectPermission.INTEGRATION_READ,
    EProjectPermission.KNOWLEDGE_READ,
    EProjectPermission.AUTOMATION_READ,
    EProjectPermission.AUTOMATION_MANAGER_READ,
    EProjectPermission.PUBLIC_ASSISTANT_READ,
    EProjectPermission.CUSTOM_INTEGRATION_READ,
    EProjectPermission.CALL_ASSISTANT_READ,
    EProjectPermission.DOC_RECOGNITION_READ,
    EProjectPermission.DASHBOARD_READ,
  ]);
  // NOTE: Either private or public assistant read shows the Assistants sidebar item.
  const hasAssistantReadPermission =
    hasPrivateAssistantReadPermission || hasPublicAssistantReadPermission;
  const { data: enabledIntegrations = [] } = useGetEnabledIntegrations();
  const isCallAssistantFeaturesEnabled = useCallAssistantFeaturesEnabled();
  const isAgentsEnabled = useAgentsEnabled();
  const isAnonymizationLabEnabled = useAnonymizationLabEnabled();
  const isCustomIntegrationEnabled = useCustomIntegrationEnabled();
  const isDocRecognitionEnabled = useDocRecognitionEnabled();
  const isSkillsEnabled = useSkillsEnabled();
  const { canReadAny: hasAnySkillReadPermission } = useSkillPermissions();
  const { mutate: moveToFolder } = useMoveToFolder();
  const { mutate: archiveChat } = useArchiveChat();
  const { mutate: unarchiveChat } = useUnarchiveChat();
  const [activeDragTitle, setActiveDragTitle] = useState<string | null>(null);
  const activeDragDataRef = useRef<TDragData | null>(null);
  const { setView, view: storedView } = useSidebarView();
  // NOTE: NSAI-6771 — the tabs follow the same assistants read access as the
  // Assistants navigation item, and a remembered tab the member may no longer
  // open resolves to the default instead of leaving the row with none active.
  const sidebarViews = useMemo(
    () => getSidebarViews({ hasAssistantReadPermission }),
    [hasAssistantReadPermission],
  );
  const view = resolveSidebarView({ view: storedView, views: sidebarViews });
  const isAssistantChatsViewAvailable = sidebarViews.includes(
    ESidebarView.ASSISTANT_CHATS,
  );

  const { pathname, state: navigationState } = useLocation();
  const handledNewChatPathRef = useRef<string | null>(null);

  // NOTE: NSAI-6472 — a chat started from the Folders tab used to open with the
  // sidebar still on Folders: the new chat was in neither visible list, and
  // because the tab is remembered it stayed that way across a reload. The
  // handler that creates the chat navigates with `isNewChat` and the assistants
  // the opening message was sent to, which says which of the two lists the chat
  // has just landed in.
  //
  // Handled once per chat: the chat page rewrites this same state moments later
  // to drop the pending payload (Chat.tsx), and re-reading it then would see no
  // assistants and pull an assistant chat back to the Chats tab.
  useEffect(() => {
    if (!navigationState?.isNewChat) {
      return;
    }

    if (handledNewChatPathRef.current === pathname) {
      return;
    }

    handledNewChatPathRef.current = pathname;

    const isAssistantChat =
      Array.isArray(navigationState.pendingAssistants) &&
      navigationState.pendingAssistants.length > 0;

    setView(
      isAssistantChat ? ESidebarView.ASSISTANT_CHATS : ESidebarView.CHATS,
    );
  }, [navigationState, pathname, setView]);

  // NOTE: Radix hands the tab value back as a bare string, so it is checked
  // against the shared view list rather than cast into the enum.
  const handleViewChange = useCallback(
    (nextValue: string): void => {
      if (isSidebarView(nextValue)) {
        setView(nextValue);
      }
    },
    [setView],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // NOTE: Require 8px drag distance so clicks still navigate normally
        distance: 8,
      },
    }),
  );

  const handleDragStart = useCallback(({ active }: DragStartEvent): void => {
    const dragData = active.data.current;
    if (dragData?.type === "chat" && typeof dragData.chatTitle === "string") {
      setActiveDragTitle(dragData.chatTitle);
    }
    // NOTE: see handleDragEnd — the payload is kept because the node it came
    // from may not survive the drag.
    activeDragDataRef.current = dragData ?? null;
  }, []);

  const handleDragEnd = useCallback(
    ({ active, over }: DragEndEvent): void => {
      setActiveDragTitle(null);
      // NOTE: NSAI-6342 — dnd-kit reads a drag's payload off the draggable
      // node, and a drag that springs the Folders tab open unmounts the pane
      // the dragged row lived in: by the time the chat lands in a folder,
      // `active.data` has been emptied and the drop no longer knows what it is
      // carrying. What the drag started with is remembered instead, and is
      // what this falls back to.
      const activeDragData = active.data.current;
      const dragData =
        activeDragData?.type === "chat"
          ? activeDragData
          : activeDragDataRef.current;
      activeDragDataRef.current = null;
      if (!over) return;
      const dropData = over.data.current;
      if (dragData?.type !== "chat" || typeof dragData.chatUuid !== "string") {
        return;
      }

      // NOTE: Dropping onto the archive zone archives the chat
      if (dropData?.type === "archive") {
        if (!dragData.isArchived) {
          archiveChat(dragData.chatUuid);
        }
        return;
      }

      // NOTE: Dropping an archived chat onto root or folder unarchives it
      if (dragData.isArchived) {
        unarchiveChat({ uuid: dragData.chatUuid });

        // NOTE: If dropped onto a folder, also move it there after unarchiving
        if (
          dropData?.type === "folder" &&
          typeof dropData.folderUuid === "string"
        ) {
          moveToFolder({
            chatUuid: dragData.chatUuid,
            folderId: dropData.folderUuid,
          });
        }
        return;
      }

      if (
        dropData?.type === "folder" &&
        typeof dropData.folderUuid === "string"
      ) {
        moveToFolder({
          chatUuid: dragData.chatUuid,
          folderId: dropData.folderUuid,
        });
      } else if (dropData?.type === "root") {
        // NOTE: Dropping on root chat list moves chat out of its folder
        moveToFolder({
          chatUuid: dragData.chatUuid,
          folderId: null,
        });
      }
    },
    [moveToFolder, archiveChat, unarchiveChat],
  );

  // NOTE: NSAI-5822 — the scrollport is whatever the header and the footer
  // leave, not a hard 100vh minus the header: the old calc did not account for
  // the account block, so the footer hung ~64px below the fold and the avatar
  // was clipped by the viewport edge.
  const mainSidebarScrollClassName = "min-h-0 flex-1";

  const mainSidebarScrollBody = (
    <SidebarContent className="overflow-visible">
      <SidebarGroup className="px-1.5 py-0">
        <SidebarMenu className="border-b py-3 group-data-[collapsible=icon]:border-b-0">
          <SidebarMenuItem>
            {/* NOTE: NSAI-5822 — `end` so "New chat" is active only on /chat
                itself. Without it the route matched every /chat/:id as well, so
                opening a chat lit up both "New chat" and the chat's own row in
                the history below. */}
            <NavLink to="/chat" end>
              {({ isActive }) => (
                <SidebarMenuButton
                  id="sidebar-new-chat"
                  size="lg"
                  isActive={isActive}
                  tooltip={t("menu.new_chat")}
                  aria-label={t("menu.new_chat")}
                  className={cn(
                    sidebarNavIconVariants({ isActive }),
                    NAV_BUTTON_CLASS,
                  )}
                >
                  <NavigationIcon icon={ENavigationIcon.NEW_CHAT} />
                  <span>{t("menu.new_chat")}</span>
                </SidebarMenuButton>
              )}
            </NavLink>
          </SidebarMenuItem>
          {hasAssistantReadPermission && (
            <SidebarMenuItem>
              <NavLink to="/assistants">
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-assistants"
                    data-testid="sidebar-assistants"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.assistant")}
                    aria-label={t("menu.assistant")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.ASSISTANTS} />
                    <span>{t("menu.assistant")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {isAgentsEnabled &&
            (hasAutomationReadPermission ||
              hasAutomationManagerReadPermission) && (
              <SidebarMenuItem>
                <NavLink to={PATHS.AGENTS}>
                  {({ isActive }) => (
                    <SidebarMenuButton
                      id="sidebar-agents"
                      data-testid="sidebar-agents"
                      size="lg"
                      isActive={isActive}
                      tooltip={t("menu.agents")}
                      aria-label={t("menu.agents")}
                      className={cn(
                        sidebarNavIconVariants({ isActive }),
                        NAV_BUTTON_CLASS,
                      )}
                    >
                      <NavigationIcon icon={ENavigationIcon.AGENTS} />
                      <span>{t("menu.agents")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            )}
          {/* NOTE: NSAI-6820 — Transcribe (/uploads) is no longer a row of its
              own: it is an action of the AI Call Assistant page, whose meetings
              it produces. This row therefore stays lit on /uploads, and carries
              the in-flight upload count — an upload does not keep the user on
              that page (NSAI-5141), so the count must stay visible from any. */}
          {isCallAssistantFeaturesEnabled && hasCallAssistantReadPermission && (
            <SidebarMenuItem>
              <NavLink to={PATHS.CALL_ASSISTANT}>
                {({ isActive }) => {
                  const isCallAssistantActive =
                    isActive || pathname.startsWith(PATHS.UPLOADS);

                  return (
                    <SidebarMenuButton
                      id="sidebar-call-assistant"
                      data-testid="sidebar-call-assistant"
                      size="lg"
                      isActive={isCallAssistantActive}
                      tooltip={t("menu.call_assistant")}
                      aria-label={t("menu.call_assistant")}
                      className={cn(
                        sidebarNavIconVariants({
                          isActive: isCallAssistantActive,
                        }),
                        NAV_BUTTON_CLASS,
                      )}
                    >
                      <NavigationIcon icon={ENavigationIcon.CALL_ASSISTANT} />
                      <span>{t("menu.call_assistant")}</span>
                      <UploadsInProgressBadge />
                    </SidebarMenuButton>
                  );
                }}
              </NavLink>
            </SidebarMenuItem>
          )}
          {hasKnowledgeReadPermission && (
            <SidebarMenuItem>
              <NavLink to={PATHS.KNOWLEDGE}>
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-knowledge"
                    data-testid="sidebar-knowledge"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.knowledge")}
                    aria-label={t("menu.knowledge")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.KNOWLEDGE} />
                    <span>{t("menu.knowledge")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {/* NOTE: AI Generated Files lists only the logged-in user's own
              files (scoped server-side by userId), so it is not gated on
              KNOWLEDGE_READ — every project member can access their files. */}
          <SidebarMenuItem>
            <NavLink to={PATHS.AI_GENERATED_FILES}>
              {({ isActive }) => (
                <SidebarMenuButton
                  id="sidebar-generated-files"
                  data-testid="sidebar-generated-files"
                  size="lg"
                  isActive={isActive}
                  tooltip={t("menu.generated_files")}
                  aria-label={t("menu.generated_files")}
                  className={cn(
                    sidebarNavIconVariants({ isActive }),
                    NAV_BUTTON_CLASS,
                  )}
                >
                  <NavigationIcon icon={ENavigationIcon.GENERATED_FILES} />
                  <span>{t("menu.generated_files")}</span>
                </SidebarMenuButton>
              )}
            </NavLink>
          </SidebarMenuItem>
          {/* NOTE: NSAI-5657 — saved dashboards sit right under AI Generated
              Files: both hold things the assistant produced in chat, and
              neither is opened often enough to earn a slot near the top.
              Gated on dashboard:read like the route itself. */}
          {hasDashboardReadPermission && (
            <SidebarMenuItem>
              <NavLink to={PATHS.DASHBOARDS}>
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-dashboards"
                    data-testid="sidebar-dashboards"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.dashboards")}
                    aria-label={t("menu.dashboards")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.DASHBOARDS} />
                    <span>{t("menu.dashboards")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {/* NOTE: NSAI-4317 — shown only when the backend flag is on AND the
              user holds doc_recognition:read. A project with no configured
              watched folder still shows the entry; the page itself explains
              that an owner has to set one up, which is more discoverable than
              a menu item that silently never appears. */}
          {isDocRecognitionEnabled && hasDocRecognitionReadPermission && (
            <SidebarMenuItem>
              <NavLink to={PATHS.DOC_RECOGNITION}>
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-doc-recognition"
                    data-testid="sidebar-doc-recognition"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.doc_recognition")}
                    aria-label={t("menu.doc_recognition")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.DOC_RECOGNITION} />
                    <span>{t("menu.doc_recognition")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {/* NOTE: NSAI-3306 — shown only when the backend flag is on AND the
              user holds private_skill:read or public_skill:read. The route
              itself is gated the same way, so a deep link cannot reach a
              surface the backend would refuse. */}
          {isSkillsEnabled && hasAnySkillReadPermission && (
            <SidebarMenuItem>
              <NavLink to={PATHS.SKILLS}>
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-skills"
                    data-testid="sidebar-skills"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.skills")}
                    aria-label={t("menu.skills")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.SKILLS} />
                    <span>{t("menu.skills")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {hasPromptLibraryReadPermission && (
            <SidebarMenuItem>
              <NavLink to="/prompt-library">
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-prompt-library"
                    data-testid="sidebar-prompt-library"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.prompt_library")}
                    aria-label={t("menu.prompt_library")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.PROMPT_LIBRARY} />
                    <span>{t("menu.prompt_library")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {isAnonymizationLabEnabled && (
            <SidebarMenuItem>
              <NavLink to={PATHS.ANONYMIZATION_LAB}>
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-anonymization-lab"
                    data-testid="sidebar-anonymization-lab"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.anonymization_lab")}
                    aria-label={t("menu.anonymization_lab")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.ANONYMIZATION_LAB} />
                    <span>{t("menu.anonymization_lab")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {hasModelReadPermission && (
            <SidebarMenuItem>
              <NavLink to="/models">
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-models"
                    data-testid="sidebar-models"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.models")}
                    aria-label={t("menu.models")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.MODELS} />
                    <span>{t("menu.models")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
          {!isPersonalProject &&
            (hasAllMembersAnalyticsReadPermission ||
              hasAnalyticsReadPermission ||
              hasProbeReadPermission) && (
              <SidebarMenuItem>
                <NavLink to="/analytics">
                  {({ isActive }) => (
                    <SidebarMenuButton
                      id="sidebar-security-analytics"
                      data-testid="sidebar-security-analytics"
                      size="lg"
                      isActive={isActive}
                      tooltip={t("menu.analytics")}
                      aria-label={t("menu.analytics")}
                      className={cn(
                        sidebarNavIconVariants({ isActive }),
                        NAV_BUTTON_CLASS,
                      )}
                    >
                      <NavigationIcon icon={ENavigationIcon.ANALYTICS} />
                      <span>{t("menu.analytics")}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            )}
          {((hasCustomIntegrationReadPermission &&
            isCustomIntegrationEnabled) ||
            (hasIntegrationReadPermission &&
              enabledIntegrations.length > 0)) && (
            <SidebarMenuItem>
              <NavLink to="/integrations">
                {({ isActive }) => (
                  <SidebarMenuButton
                    id="sidebar-integrations"
                    data-testid="sidebar-integrations"
                    size="lg"
                    isActive={isActive}
                    tooltip={t("menu.integrations")}
                    aria-label={t("menu.integrations")}
                    className={cn(
                      sidebarNavIconVariants({ isActive }),
                      NAV_BUTTON_CLASS,
                    )}
                  >
                    <NavigationIcon icon={ENavigationIcon.INTEGRATIONS} />
                    <span>{t("menu.integrations")}</span>
                  </SidebarMenuButton>
                )}
              </NavLink>
            </SidebarMenuItem>
          )}
        </SidebarMenu>
      </SidebarGroup>
      <SidebarGroup className="px-1.5 py-0 grow group-data-[collapsible=icon]:hidden">
        {/* NOTE: NSAI-6342 — chats, assistant chats and folders are three
            tabs of one pane instead of three sections stacked in it. Stacked,
            each one pushed the next down by however much it happened to hold,
            so how much chat history a member could see depended on how many
            folders they kept; the chat list also needed a heading of its own
            to say which of the three you were looking at. One pane at a time
            gives every list the whole scrollport and makes the tab row that
            heading. Mobile made the same split first (NSAI-5622) — the view
            enum, the order and the labels are shared. */}
        <Tabs
          value={view}
          onValueChange={handleViewChange}
          className="flex min-w-0 grow flex-col pb-3"
          data-testid="sidebar-view-tabs"
        >
          <SidebarViewSwitch onSpringLoad={setView} views={sidebarViews} />
          <TabsContent value={ESidebarView.CHATS} className="mt-0">
            <SidebarMenu
              className="pt-4"
              data-testid="sidebar-chat-history-panel"
            >
              <ChatList view={ESidebarView.CHATS} />
            </SidebarMenu>
          </TabsContent>
          {isAssistantChatsViewAvailable && (
            <TabsContent value={ESidebarView.ASSISTANT_CHATS} className="mt-0">
              <SidebarMenu
                className="pt-4"
                data-testid="sidebar-assistant-chats-panel"
              >
                <ChatList view={ESidebarView.ASSISTANT_CHATS} />
              </SidebarMenu>
            </TabsContent>
          )}
          <TabsContent value={ESidebarView.FOLDERS} className="mt-0">
            <SidebarMenu className="pt-4">
              <ChatFolderList />
            </SidebarMenu>
          </TabsContent>
        </Tabs>
      </SidebarGroup>
    </SidebarContent>
  );

  return (
    <Sidebar
      side={dir === "rtl" ? "right" : "left"}
      collapsible="icon"
      data-testid="sidebar"
    >
      {/* NOTE: NSAI-4345 — the logo and the collapse control live inside the
          sidebar in the new design, not in a fixed strip overlaying the page.
          Collapsed, only the trigger stays, centred in the rail. */}
      <SidebarHeader className="h-[var(--top-navbar-height)] shrink-0 px-4 w-full hidden md:flex flex-row items-center justify-between group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:justify-center">
        <Link
          to={PATHS.CHAT}
          className="min-w-0 group-data-[collapsible=icon]:hidden"
          data-testid="sidebar-logo"
        >
          <Logo className="h-8 w-auto" />
        </Link>
        {/* NOTE: A click leaves the pointer hovering over this square. Arm its
            hover reveal only after the pointer leaves, so collapsing shows the
            favicon immediately instead of leaving the toggle icon on screen. */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                className="group/brand relative flex size-10 shrink-0 items-center justify-center"
                onPointerLeave={() => setIsHoverRevealArmed(true)}
                data-testid="sidebar-brand"
              >
                {railFaviconUrl && (
                  <img
                    src={railFaviconUrl}
                    alt=""
                    fetchPriority="high"
                    onLoad={() => setLoadedFaviconUrl(railFaviconUrl)}
                    onError={() => setLoadedFaviconUrl(null)}
                    className={cn(
                      "pointer-events-none absolute max-h-8 max-w-8 object-contain [@media(hover:none)]:opacity-0",
                      isFaviconReady && !isKeyboardFocusVisible
                        ? "opacity-100"
                        : "opacity-0",
                      isHoverRevealArmed && "group-hover/brand:opacity-0",
                      "group-data-[collapsible=icon]:block hidden",
                      // NOTE: NSAI-6651 — Oplot's navy mark is drawn white at
                      // night, which is exactly its own dark favicon.
                      railFaviconUrl === OPLOT_RAIL_MARK_URL &&
                        "dark:brightness-0 dark:invert",
                    )}
                    data-testid="sidebar-collapsed-favicon"
                  />
                )}
                {/* NOTE: NSAI-5822 — the trigger matches the rail's 40px icon
                    rhythm so it reads as the first item of the rail rather than
                    a stray glyph. */}
                <SidebarTrigger
                  data-testid="sidebar-toggle"
                  variant="ghost"
                  onClick={() => setIsHoverRevealArmed(false)}
                  onFocus={(event) =>
                    setIsKeyboardFocusVisible(
                      event.currentTarget.matches(":focus-visible"),
                    )
                  }
                  onBlur={() => setIsKeyboardFocusVisible(false)}
                  // NOTE: NSAI-6259 — the trigger takes the rail's own ink, not
                  // `--primary`. In dark that token is a pale lavender, so the
                  // one glyph at the top of the rail was purple while every icon
                  // under it was white: it read as an accent on a control that
                  // is pure chrome.
                  className={cn(
                    "h-10 w-10 shrink-0 rounded-xl text-sidebar-foreground transition-[color,background-color] duration-200 ease-out hover:bg-sidebar-accent hover:text-sidebar-accent-foreground motion-reduce:transition-none [&>svg]:h-5 [&>svg]:w-5",
                    isFaviconReady &&
                      !isKeyboardFocusVisible &&
                      "group-data-[collapsible=icon]:opacity-0",
                    isHoverRevealArmed &&
                      "group-data-[collapsible=icon]:group-hover/brand:opacity-100",
                    "group-data-[collapsible=icon]:[@media(hover:none)]:opacity-100",
                  )}
                />
              </div>
            </TooltipTrigger>
            {/* NOTE: To the side, never below — a tooltip under this square
                covers the first item of the rail, which is where the pointer is
                most likely heading next. */}
            <TooltipContent side="right" data-testid="sidebar-toggle-tooltip">
              <p>
                {sidebarState === "collapsed"
                  ? t("toggle.open")
                  : t("toggle.close")}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </SidebarHeader>
      {/* NOTE: NSAI-6342 — the drag context wraps the scrollport AND the docked
          archive row below it, because archiving a chat is a drag from inside
          the list onto a row that deliberately sits outside it. */}
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        // NOTE: NSAI-6342 — droppables are re-measured as the drag runs, not
        // only when it starts. Holding a chat over the Folders tab mounts a
        // whole pane of folder rows mid-drag, and a droppable measured before
        // it existed has no rectangle to hit: the drop would land on nothing
        // and the chat would go back where it came from.
        measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
      >
        <div
          data-testid="main-sidebar-scroll"
          className={cn(
            mainSidebarScrollClassName,
            "overflow-x-hidden",
            isRunning
              ? "!overflow-hidden [&_*]:!overflow-hidden"
              : // NOTE: NSAI-5822 — the stable gutter reserves scrollbar width on
                // the inline end, which in the collapsed rail pulled the whole
                // icon column ~5px off centre against the header trigger and the
                // account avatar. The rail has no room to spare, so it drops the
                // reservation and lets the (rarely needed) scrollbar overlay.
                "themed-scrollbar overflow-y-auto [scrollbar-gutter:stable] group-data-[collapsible=icon]:[scrollbar-gutter:auto]",
          )}
        >
          {mainSidebarScrollBody}
        </div>
        {/* NOTE: NSAI-6342 — the archive belongs to the chats, so it is shown
            with them and not beside the assistant chats or the folders. It is
            pinned here rather than left in the list: below however many chats
            a member has, it read as detached from the group above it and went
            out of reach as the list grew. */}
        {view === ESidebarView.CHATS && <ArchivedChats />}
        <DragOverlay dropAnimation={null}>
          {activeDragTitle && (
            <div className="flex max-w-full min-w-0 items-center gap-2 rounded-md border border-border bg-accent px-3 py-2 text-sm shadow-md">
              <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{activeDragTitle}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>
      {/* NOTE: NSAI-4345 — the account sits at the foot of the sidebar in the
          new design, not in the top-right header. */}
      <SidebarFooter className="shrink-0 border-t p-3 group-data-[collapsible=icon]:items-center group-data-[collapsible=icon]:px-2 group-data-[collapsible=icon]:py-3">
        {/* NOTE: NSAI-6401 — the account menu renders in a portal, so it cannot
            see the rail's `data-collapsible` for itself and is told instead.
            Mobile is excluded deliberately: there the sidebar is a sheet with
            no `data-collapsible` at all, so the capsule keeps its name however
            `state` reads. */}
        <UserMenu
          hasDetails
          isRailCollapsed={!isMobile && sidebarState === "collapsed"}
        />
      </SidebarFooter>
    </Sidebar>
  );
};

export const MainSidebar = memo(MainSidebarComponent);
